import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Sparkles, Zap, Cpu, Rocket, Bot } from "lucide-react";
import AnimeCharacter from "./AnimeCharacter";
import SpeechBubble from "./SpeechBubble";
import { profile } from "../../data/portfolio";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

// Slider content — rotates every ~4.5s
const slides = [
  {
    icon: Bot,
    kicker: "GenAI · Agentic AI",
    title: (
      <>
        Hi, I&apos;m <span className="text-gradient-violet">Siva</span>.
      </>
    ),
    subtitle: "I build enterprise GenAI & Agentic AI systems that actually ship to production.",
    stat: { value: "35+", label: "Enterprise projects delivered" },
  },
  {
    icon: Cpu,
    kicker: "Data Platforms",
    title: (
      <>
        From <span className="text-gradient-violet">RFP whiteboard</span> to 1PB production.
      </>
    ),
    subtitle: "13+ years architecting Data Lakes, streaming pipelines and MLOps for BFSI, Telecom, Retail & Healthcare.",
    stat: { value: "1 PB", label: "Largest data lake shipped" },
  },
  {
    icon: Zap,
    kicker: "AWS · Bedrock · SageMaker",
    title: (
      <>
        LLMs, RAG & <span className="text-gradient-violet">Vector DBs</span> — battle-tested.
      </>
    ),
    subtitle: "Bedrock chatbots, Agentic workflows, SageMaker MLOps, FinOps and computer vision — end-to-end.",
    stat: { value: "$8M+", label: "Revenue pipeline influenced" },
  },
  {
    icon: Rocket,
    kicker: "Delivery Leadership",
    title: (
      <>
        Led <span className="text-gradient-violet">30+ engineers</span> across Data · AI · Cloud.
      </>
    ),
    subtitle: "Architecture governance, cost models, HLD/LLD and pre-sales that convert client trust into revenue.",
    stat: { value: "$4M+", label: "New revenue acquired" },
  },
];

const BackgroundFX = () => (
  <>
    {/* Aurora blobs */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
      <div className="aurora-blob aurora-1" style={{ top: "10%", left: "5%" }} />
      <div className="aurora-blob aurora-2" style={{ top: "50%", right: "5%" }} />
      <div className="aurora-blob aurora-3" style={{ bottom: "-10%", left: "40%" }} />

      {/* Falling stars */}
      {Array.from({ length: 14 }).map((_, i) => (
        <span
          key={i}
          className="falling"
          style={{
            left: `${(i * 7 + 5) % 100}%`,
            "--fall-t": `${6 + (i % 5)}s`,
            "--fall-d": `${(i * 0.7).toFixed(2)}s`,
          }}
        />
      ))}
    </div>
    <div className="absolute inset-0 grid-lines pointer-events-none" aria-hidden />
  </>
);

export const Hero = () => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 4800);
    return () => clearInterval(t);
  }, []);

  const s = slides[idx];
  const Icon = s.icon;

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen hero-bg pt-28 md:pt-32 pb-20 overflow-hidden"
    >
      <BackgroundFX />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col items-center text-center">
          {/* Character with orbits */}
          <div className="relative w-[320px] sm:w-[380px] md:w-[440px] aspect-square mb-4">
            {/* Conic rings */}
            <div className="conic-ring" aria-hidden />
            <div className="conic-ring-2" aria-hidden />

            {/* Orbit dots */}
            <div className="absolute inset-0" aria-hidden>
              <span className="orbit-dot bg-[color:var(--primary)]" style={{ "--orbit-r": "180px", "--orbit-t": "9s", boxShadow: "0 0 12px #A855F7" }} />
              <span className="orbit-dot bg-[color:var(--secondary)]" style={{ "--orbit-r": "200px", "--orbit-t": "12s", boxShadow: "0 0 12px #EC4899", animationDelay: "-3s" }} />
              <span className="orbit-dot bg-[color:var(--accent)]" style={{ "--orbit-r": "160px", "--orbit-t": "7s", boxShadow: "0 0 12px #FDE68A", animationDelay: "-1.5s" }} />
              <span className="orbit-dot bg-[color:var(--primary)]" style={{ "--orbit-r": "220px", "--orbit-t": "16s", boxShadow: "0 0 10px #C084FC", animationDelay: "-6s" }} />
            </div>

            {/* Character */}
            <div className="absolute inset-0 flex items-center justify-center">
              <AnimeCharacter />
            </div>

            {/* Speech bubble (top-left of character) */}
            <div className="absolute -top-2 -left-4 md:-left-14 hidden sm:block">
              <SpeechBubble lines={profile.taglines} />
            </div>

            {/* Floating stat chip (bottom-right) */}
            <motion.div
              key={s.stat.value}
              initial={{ opacity: 0, y: 10, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="absolute bottom-4 -right-2 md:right-0 glass-strong rounded-2xl px-4 py-3 glow-primary hidden sm:block"
              data-testid="hero-stat-chip"
            >
              <div className="text-[10px] font-display uppercase tracking-widest text-white/60">{s.stat.label}</div>
              <div className="font-display text-2xl text-[color:var(--accent)] text-glow-accent">{s.stat.value}</div>
            </motion.div>
          </div>

          {/* Sliding intro card */}
          <div className="relative w-full max-w-3xl h-[280px] sm:h-[240px] md:h-[220px]" data-testid="hero-slider">
            <AnimatePresence mode="wait">
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 flex flex-col items-center"
              >
                <div className="inline-flex items-center gap-2 glass rounded-full px-3 py-1.5 mb-5">
                  <Icon size={14} className="text-[color:var(--accent)]" />
                  <span className="text-[11px] font-display tracking-widest uppercase text-white/70">
                    {s.kicker}
                  </span>
                </div>

                <h1
                  data-testid="hero-heading"
                  className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-extrabold text-white max-w-3xl"
                >
                  {s.title}
                </h1>

                <p
                  data-testid="hero-subtitle"
                  className="mt-5 max-w-2xl text-white/70 text-base md:text-lg font-body leading-relaxed"
                >
                  {s.subtitle}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Slider dots */}
          <div className="mt-3 flex items-center gap-2" data-testid="hero-slider-dots">
            {slides.map((_, i) => (
              <button
                key={i}
                data-testid={`hero-slider-dot-${i}`}
                onClick={() => setIdx(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-8 bg-[color:var(--primary)]" : "w-3 bg-white/25 hover:bg-white/50"
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>

          {/* CTAs */}
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <button
              data-testid="hero-cta-projects"
              onClick={() => scrollTo("projects")}
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[color:var(--primary)] text-white font-display text-sm uppercase tracking-widest hover:bg-[color:var(--primary-2)] transition-all glow-primary"
            >
              See Projects
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              data-testid="hero-cta-contact"
              onClick={() => scrollTo("contact")}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 text-white font-display text-sm uppercase tracking-widest hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-all"
            >
              Hire Me
            </button>
          </div>

          {/* Meta strip */}
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 justify-center text-[11px] font-display uppercase tracking-widest text-white/50">
            <span><Sparkles size={12} className="inline mb-0.5 mr-1 text-[color:var(--accent)]" />Available · Chennai · IN</span>
            <span><span className="text-[color:var(--primary)]">●</span> AWS · Bedrock · SageMaker</span>
            <span><span className="text-[color:var(--secondary)]">●</span> LangChain · CrewAI · RAG</span>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/40 text-xs font-display tracking-widest uppercase z-10">
        scroll ↓
      </div>
    </section>
  );
};

export default Hero;